import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { DialogLayoutComponent } from "@obg/mat.ui.core";
import {
	CASINO_CLASSIFICATIONS,
	CasinoBlockSelector,
	CORE_ROUTE,
	ProductBlockAction,
	SPORTSBOOK_CLASSIFICATIONS,
	SportsbookBlockSelector,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { unionBy } from "lodash-es";
import { combineLatest, Observable, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";

import { PLAYER_PROFILE } from "../select-player-profile.const";

export const SELECT_PLAYER_PROFILE_DIALOG_ID = "select-player-profile";

@Component({
	selector: "obg-m-select-player-profile-dialog-container",
	templateUrl: "./select-player-profile-dialog.container.html",
	styleUrls: ["./select-player-profile-dialog.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class SelectPlayerProfileDialogContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-select-player-profile-dialog") hostCssClass = true;
	@ViewChild("dialog", { static: true }) dialogLayout!: DialogLayoutComponent;

	hideTitle$: Observable<boolean | undefined> | undefined;
	playerProfile = PLAYER_PROFILE.allProducts;
	dialogId = SELECT_PLAYER_PROFILE_DIALOG_ID;

	private data$$: Subscription | undefined;
	private close$$: Subscription | undefined;

	constructor(
		private store: Store<CoreContentState>,
		private casinoBlockSelector: CasinoBlockSelector
	) {}

	ngOnInit() {
		this.data$$ = combineLatest([
			this.store.pipe(select(this.casinoBlockSelector.shouldChoosePlayerProfile())),
			this.store.select(SportsbookBlockSelector.shouldChoosePlayerProfile),
		])
			.pipe(
				tap(([casinoShouldChoose, sportsbookShouldChoose]) => {
					if (!casinoShouldChoose && !sportsbookShouldChoose) {
						this.closeDialog();
					}
				})
			)
			.subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.close$$?.unsubscribe();
	}

	confirmSelection() {
		const isCasinoBlocked = this.playerProfile === PLAYER_PROFILE.sportsbookOnly;
		const isSportsbookBlocked = this.playerProfile === PLAYER_PROFILE.casinoOnly;
		this.store.dispatch(
			ProductBlockAction.update(
				unionBy(
					[{ classificationName: "Unclassified", isBlocked: !isSportsbookBlocked }],
					CASINO_CLASSIFICATIONS.map((classificationName) => ({
						classificationName,
						isBlocked: isCasinoBlocked,
					})),
					SPORTSBOOK_CLASSIFICATIONS.map((classificationName) => ({
						classificationName,
						isBlocked: isSportsbookBlocked,
					})),
					"classificationName"
				)
			)
		);
		this.closeDialog();
	}

	askMeLater() {
		this.closeDialog();
	}

	isConfirmDisabled(): boolean {
		return this.playerProfile === PLAYER_PROFILE.allProducts;
	}

	private closeDialog() {
		this.close$$ = this.store
			.pipe(
				RouteSelector.getName$,
				take(1),
				tap((name) =>
					name === CORE_ROUTE.responsibleGaming.selectPlayerProfile
						? this.store.dispatch(RouteAction.back())
						: this.dialogLayout.close()
				)
			)
			.subscribe();
	}
}
